import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { ShieldCheck } from 'lucide-react';
import companyAdminService from '../../services/companyAdminService';
import { usePermissions } from '../../context/PermissionsContext';
import { LIMITS_COPY } from '../../constants/partnerCopy';
import PermissionCheckboxGroup from '../PlatformAdmin/PermissionCheckboxGroup';
import { platformMcpTooltips } from '../../utils/platformMcpPermission';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

const KEY_TOOLTIPS = platformMcpTooltips('companyLimits');

/**
 * Read-only entitlements view (spec §5.3). Shows the areas enabled for the
 * company (its envelope). Helps company admins understand why an area might be
 * missing from the role builder. For a partner's client company this is the
 * enforced set: its own limits ∩ the partner's (Partner tier guide §7.1).
 */
const CompanyEntitlementsPanel = ({ notify, catalog }) => {
  const { partner, isPartnerStaff } = usePermissions();
  const partnerManaged = !!partner && !isPartnerStaff;
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await companyAdminService.getEntitlements();
        if (!cancelled) setKeys(Array.isArray(data?.permission_keys) ? data.permission_keys : []);
      } catch (err) {
        if (!cancelled) notify(err?.message || 'Failed to load entitlements.', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notify]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box className={styles.noteBanner}>
        <ShieldCheck size={18} style={{ flexShrink: 0, marginTop: 1 }} />
        {partnerManaged ? (
          <span>
            These are the areas enabled for your company. {LIMITS_COPY.companyAdminHelp(partner.name)} You
            can only grant these areas in your custom roles — if something you need is missing, ask your
            partner.
          </span>
        ) : (
          <span>
            These are the areas enabled for your company, managed by the platform. You can only grant
            these areas in your custom roles — if something you need is missing, ask the platform admin
            to enable it.
          </span>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {keys.length} area{keys.length === 1 ? '' : 's'} enabled
          </Typography>
          <PermissionCheckboxGroup catalog={catalog} selected={keys} keyTooltips={KEY_TOOLTIPS} readOnly />
        </>
      )}
    </Box>
  );
};

export default CompanyEntitlementsPanel;
